import { HelpLink } from "@components/help/HelpLink";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { REQUIRED_LEVEL_ANCHOR_LABELS } from "@lib/constants/scoringScales";
import { GripVertical, Plus, Save, Trash2, X } from "lucide-react";
import { useId, useState } from "react";
import { Link } from "react-router";

interface Dimension {
  name: string;
  description: string;
  idealScore: number | null;
  positiveIndicators: string;
  negativeIndicators: string;
  successCriteria: string;
  // Carries the continuity-id of the dimension being edited so scores/bands
  // FK'ing this continuity follow the edit. Undefined for newly-added rows —
  // the server assigns a fresh id.
  continuityId?: string;
}

interface CapabilityDimensionsEditorProps {
  initial: Dimension[];
  onSave: (dimensions: Dimension[]) => void;
  onCancel: () => void;
  saving: boolean;
}

interface KeyedDimension extends Dimension {
  _key: string;
}

const EMPTY_DIMENSION: Dimension = {
  name: "",
  description: "",
  idealScore: null,
  positiveIndicators: "",
  negativeIndicators: "",
  successCriteria: "",
};

const MAX_DIMENSIONS = 6;
const RECOMMENDED_DIMENSIONS = 5;

const ANCHOR_LABELS = REQUIRED_LEVEL_ANCHOR_LABELS;

export function CapabilityDimensionsEditor({
  initial,
  onSave,
  onCancel,
  saving,
}: CapabilityDimensionsEditorProps) {
  const keyPrefix = useId();
  const [nextKey, setNextKey] = useState(initial.length);
  const [dimensions, setDimensions] = useState<KeyedDimension[]>(
    (initial.length > 0 ? initial : [EMPTY_DIMENSION]).map((d, i) => ({
      ...d,
      _key: `${keyPrefix}-${i}`,
    })),
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const addDimension = () => {
    setDimensions([...dimensions, { ...EMPTY_DIMENSION, _key: `${keyPrefix}-${nextKey}` }]);
    setNextKey((n) => n + 1);
  };

  const removeDimension = (index: number) =>
    setDimensions(dimensions.filter((_, i) => i !== index));

  const updateDimension = (index: number, field: keyof Dimension, value: string | number | null) =>
    setDimensions(dimensions.map((d, i) => (i === index ? { ...d, [field]: value } : d)));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = dimensions.findIndex((d) => d._key === active.id);
    const newIndex = dimensions.findIndex((d) => d._key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setDimensions(arrayMove(dimensions, oldIndex, newIndex));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(dimensions.filter((d) => d.name.trim() !== "").map(({ _key, ...rest }) => rest));
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mx-auto w-full max-w-3xl space-y-3 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-baseline gap-3">
          <h3 className="text-sm font-medium text-text-secondary">Capability Dimensions</h3>
          <span
            className={`text-xs ${dimensions.length >= RECOMMENDED_DIMENSIONS ? "text-amber-600" : "text-text-faint"}`}
          >
            {dimensions.length} / {MAX_DIMENSIONS} dimensions
          </span>
          <Link to="/help/scoring-scales" className="text-xs text-text-faint hover:text-accent">
            How required levels work →
          </Link>
          <Link to="/help/role-blueprint" className="text-xs text-text-faint hover:text-accent">
            About the blueprint →
          </Link>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-text-muted hover:bg-card-hover"
          >
            <X size={12} /> Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="flex items-center gap-1 rounded bg-accent px-3 py-1 text-xs font-medium text-white hover:bg-accent/90 disabled:opacity-50"
          >
            <Save size={12} /> {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext
          items={dimensions.map((d) => d._key)}
          strategy={verticalListSortingStrategy}
        >
          <div className="space-y-3">
            {dimensions.map((dim, i) => (
              <SortableDimensionRow
                key={dim._key}
                id={dim._key}
                dim={dim}
                index={i}
                canRemove={dimensions.length > 1}
                onChange={updateDimension}
                onRemove={removeDimension}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {dimensions.length >= MAX_DIMENSIONS ? (
        <p
          role="status"
          className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
        >
          <span>
            You've reached the cap of {MAX_DIMENSIONS} dimensions. To make room for another,
            consolidate two of the existing dimensions into one — assessment quality degrades
            sharply beyond {MAX_DIMENSIONS}.
          </span>
          <HelpLink to="/help/scoring-scales" label="Why six dimensions" />
        </p>
      ) : dimensions.length >= RECOMMENDED_DIMENSIONS ? (
        <p
          role="status"
          className="flex items-start gap-1.5 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
        >
          <span>
            {RECOMMENDED_DIMENSIONS} dimensions is the recommended ceiling for assessability. Before
            adding a sixth, consider whether two of the existing dimensions could be consolidated
            into one.
          </span>
          <HelpLink to="/help/scoring-scales" label="Why five is the recommended ceiling" />
        </p>
      ) : null}

      <button
        type="button"
        onClick={addDimension}
        disabled={dimensions.length >= MAX_DIMENSIONS}
        title={
          dimensions.length >= MAX_DIMENSIONS
            ? "Cap reached — consolidate two dimensions to add another"
            : undefined
        }
        className="flex items-center gap-1 text-xs text-accent hover:text-accent/80 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-accent"
      >
        <Plus size={12} /> Add dimension
      </button>
    </form>
  );
}

interface SortableDimensionRowProps {
  id: string;
  dim: KeyedDimension;
  index: number;
  canRemove: boolean;
  onChange: (index: number, field: keyof Dimension, value: string | number | null) => void;
  onRemove: (index: number) => void;
}

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[11px] font-medium uppercase tracking-wide text-text-faint"
    >
      {children}
    </label>
  );
}

function SortableDimensionRow({
  id,
  dim,
  index,
  canRemove,
  onChange,
  onRemove,
}: SortableDimensionRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  const inputClass =
    "w-full rounded border border-input-border bg-card px-2 py-1.5 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent";
  const textareaClass = `${inputClass} text-xs`;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 rounded border border-border bg-card p-3"
    >
      <button
        type="button"
        aria-label={`Reorder dimension ${index + 1}`}
        className="mt-1 flex-shrink-0 cursor-grab touch-none rounded p-0.5 text-text-faint hover:text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical size={16} />
      </button>
      <div className="flex-1 space-y-3">
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <FieldLabel htmlFor={`${id}-name`}>Name</FieldLabel>
            <input
              id={`${id}-name`}
              type="text"
              value={dim.name}
              onChange={(e) => onChange(index, "name", e.target.value)}
              className={inputClass}
              placeholder="e.g. Strategic Vision"
            />
          </div>
          {canRemove && (
            <button
              type="button"
              onClick={() => onRemove(index)}
              aria-label={`Remove dimension ${index + 1}`}
              className="mt-5 flex-shrink-0 rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
            >
              <Trash2 size={14} />
            </button>
          )}
        </div>

        <div>
          <FieldLabel htmlFor={`${id}-description`}>Description</FieldLabel>
          <input
            id={`${id}-description`}
            type="text"
            value={dim.description}
            onChange={(e) => onChange(index, "description", e.target.value)}
            className={inputClass}
            placeholder="One-line summary of what this dimension covers"
          />
        </div>

        <IdealLevelSelector
          id={`${id}-ideal`}
          value={dim.idealScore}
          onChange={(v) => onChange(index, "idealScore", v)}
        />

        <div>
          <FieldLabel htmlFor={`${id}-positive`}>What good looks like</FieldLabel>
          <textarea
            id={`${id}-positive`}
            value={dim.positiveIndicators}
            onChange={(e) => onChange(index, "positiveIndicators", e.target.value)}
            className={textareaClass}
            placeholder="Observable behaviours that signal strength in this dimension"
            rows={4}
          />
        </div>

        <div>
          <FieldLabel htmlFor={`${id}-negative`}>Red flags</FieldLabel>
          <textarea
            id={`${id}-negative`}
            value={dim.negativeIndicators}
            onChange={(e) => onChange(index, "negativeIndicators", e.target.value)}
            className={textareaClass}
            placeholder="Anti-patterns or warning signs to watch for"
            rows={4}
          />
        </div>

        <div>
          <FieldLabel htmlFor={`${id}-success`}>Success criteria</FieldLabel>
          <textarea
            id={`${id}-success`}
            value={dim.successCriteria}
            onChange={(e) => onChange(index, "successCriteria", e.target.value)}
            className={textareaClass}
            placeholder="Concrete evidence of competence (e.g. 'Has led a P&L of £50M+')"
            rows={4}
          />
        </div>
      </div>
    </div>
  );
}

interface IdealLevelSelectorProps {
  id: string;
  value: number | null;
  onChange: (v: number | null) => void;
}

function IdealLevelSelector({ id, value, onChange }: IdealLevelSelectorProps) {
  return (
    <div>
      <span
        id={`${id}-label`}
        className="block text-[11px] font-medium uppercase tracking-wide text-text-faint"
      >
        Ideal Level
      </span>
      <div role="radiogroup" aria-labelledby={`${id}-label`} className="mt-1 flex flex-wrap gap-1">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
          const selected = value === n;
          return (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={selected}
              aria-label={`Level ${n} — ${ANCHOR_LABELS[n]}`}
              onClick={() => onChange(selected ? null : n)}
              className={`flex h-8 w-8 items-center justify-center rounded text-xs font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-accent ${levelButtonClass(n, selected)}`}
            >
              {n}
            </button>
          );
        })}
      </div>
      <p className="mt-1.5 min-h-[1rem] text-xs text-text-secondary">
        {value != null ? (
          <>
            <span className="font-medium text-text-primary">{value}</span>
            {" — "}
            {ANCHOR_LABELS[value]}
          </>
        ) : (
          <span className="text-text-faint">Pick a level (1 minimal · 10 expert)</span>
        )}
      </p>
    </div>
  );
}

function levelButtonClass(level: number, selected: boolean): string {
  if (!selected) {
    return "border border-input-border bg-card text-text-secondary hover:border-accent hover:text-text-primary";
  }
  if (level >= 8) {
    return "bg-emerald-600 text-white ring-2 ring-emerald-600";
  }
  if (level >= 6) {
    return "bg-emerald-400 text-white ring-1 ring-emerald-400";
  }
  return "bg-accent text-white";
}
